import type { ResultType } from '../../../../constants/global';
import type {
	AgentTrustSource,
	AgentTrustState,
	IAgentProfile,
	IAgentProfileAssignment,
	IAgentProfileTrust,
} from '../../../../interface/agent-profile';
import type { IProfileData } from '../../../../interface/state/IAuthState';
import type { IElectionOfficialByQuery } from '../../../../interface/state/IElectionState';

const str = (v: unknown): string | null => {
	if (typeof v !== 'string') return null;
	const t = v.trim();
	// api.core serializes unset fields as the literal string "null" in places.
	return !t || t === 'null' ? null : t;
};

const bool = (v: unknown): boolean => v === true;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * ISO date with the time dropped. The server column is non-nullable, so an unset
 * date arrives as year 0001 rather than null.
 */
const isoDate = (v: unknown): string | null => {
	const s = str(v);
	if (!s || s.startsWith('0001')) return null;
	return s.slice(0, 10);
};

const assignmentFrom = (raw: unknown): IAgentProfileAssignment | null => {
	if (!isObject(raw)) return null;
	const code = str(raw.code) ?? str(raw.entityCode);
	const resultType = str(raw.resultType);
	if (!code && !resultType) return null;

	return {
		electionId: typeof raw.electionId === 'number' ? raw.electionId : null,
		electionName: str(raw.electionName) ?? str(raw.election),
		resultType: resultType ? (resultType.toUpperCase() as ResultType) : null,
		code,
		name: str(raw.name),
		isCompleted: bool(raw.isCompleted),
		stateName: str(raw.stateName),
		lgaName: str(raw.lgaName),
		wardName: str(raw.wardName),
		pollingUnitName: str(raw.pollingUnitName),
	};
};

const TRUST_STATES: readonly AgentTrustState[] = ['NONE', 'CLAIMED', 'TRUSTED', 'REMOVED'];
const TRUST_SOURCES: readonly AgentTrustSource[] = ['LINK', 'INVITATION', 'MANUAL'];

/** An unknown state is dropped rather than guessed at — the UI then says nothing. */
const trustFrom = (raw: unknown): IAgentProfileTrust | null => {
	if (!isObject(raw)) return null;
	const state = str(raw.state)?.toUpperCase() as AgentTrustState | undefined;
	if (!state || !TRUST_STATES.includes(state)) return null;
	const source = str(raw.source)?.toUpperCase() as AgentTrustSource | undefined;

	return {
		state,
		source: source && TRUST_SOURCES.includes(source) ? source : null,
		isTrusted: bool(raw.isTrusted),
		changedAtUtc: str(raw.changedAtUtc),
	};
};

/**
 * Shape-validates and normalizes a wire body into `IAgentProfile`.
 *
 * Returns null for anything that is not an object carrying an `identity` object. That
 * guard is not paranoia: when a route collides, these services return an HTML string,
 * and a bare property access on a string resolves to `String.prototype` methods — which
 * React then invokes as a lazy state initializer and crashes on.
 */
export const normalizeAgentProfile = (body: unknown): IAgentProfile | null => {
	if (!isObject(body)) return null;
	const root = isObject(body.data) ? body.data : body;
	if (!isObject(root)) return null;

	const identity = root.identity;
	if (!isObject(identity)) return null;

	const party = isObject(root.party) ? root.party : null;
	const payout = isObject(root.payout) ? root.payout : null;
	const verification = isObject(root.verification) ? root.verification : null;
	const readiness = isObject(root.readiness) ? root.readiness : null;

	return {
		identity: {
			userId: str(identity.userId) ?? '',
			firstName: str(identity.firstName) ?? '',
			lastName: str(identity.lastName) ?? '',
			userName: str(identity.userName) ?? '',
			email: str(identity.email) ?? '',
			phoneNumber: str(identity.phoneNumber),
			gender: str(identity.gender),
			dateOfBirth: isoDate(identity.dateOfBirth),
			isActive: bool(identity.isActive),
			roles: Array.isArray(identity.roles)
				? identity.roles.map((r) => (isObject(r) ? (str(r.name) ?? '') : (str(r) ?? ''))).filter(Boolean)
				: [],
			profilePhotoUrl: str(identity.profilePhotoUrl),
		},
		party: party
			? {
					partyId: str(party.partyId),
					partyName: str(party.partyName),
					partyLongName: str(party.partyLongName),
					partyLogoUrl: str(party.partyLogoUrl),
				}
			: null,
		assignment: assignmentFrom(root.deployment ?? root.assignment),
		payout: payout
			? {
					bankName: str(payout.bankName),
					accountName: str(payout.accountName),
					accountNumberMasked: str(payout.accountNumberMasked),
					hasBankDetails: bool(payout.hasBankDetails),
				}
			: null,
		verification: verification
			? {
					votersCardNumberMasked: str(verification.votersCardNumberMasked),
					hasVotersCard: bool(verification.hasVotersCard),
					hasProfilePhoto: bool(verification.hasProfilePhoto),
					needsManualReview: bool(verification.kycReviewRequired ?? verification.needsManualReview),
					onboardingCompletedAt: isoDate(verification.onboardingCompletedAt),
					isApproved: bool(verification.isApproved),
					isQualified: bool(verification.isQualified),
				}
			: null,
		readiness: readiness
			? {
					hasSmartPhoneWithInternet: bool(readiness.hasSmartPhoneWithInternet),
					isAvailableAllDay: bool(readiness.isAvailableAllDay),
					hasPartyAgentExperience: bool(readiness.hasPartyAgentExperience),
					isNativeSpeaker: bool(readiness.isNativeSpeaker),
					hasPowerBank: bool(readiness.hasPowerBank),
				}
			: null,
		// Absent on api.core builds that predate trust; keep that distinct from an explicit null.
		...('trust' in root ? { trust: trustFrom(root.trust) } : {}),
	};
};

/**
 * Builds the same shape from what the app already holds — the ten-field
 * `User/GetUserByEmail` payload plus the assignment already in Redux from the layout's
 * `get_ElectionOfficial` fetch.
 *
 * This is what makes the page useful before the agent-profile endpoint rolls out: the
 * entity-code ladder needs only `assignment.code` and `resultType`, both of which are
 * already in state. The groups this source genuinely cannot answer stay `null`, so
 * they render as unavailable rather than as the agent's unfinished business.
 */
export const fromLegacyProfile = (
	legacy: IProfileData | null | undefined,
	official?: IElectionOfficialByQuery | null
): IAgentProfile | null => {
	if (!legacy) return null;

	const a = official?.assignment;

	return {
		identity: {
			userId: legacy.userId ?? '',
			firstName: legacy.firstName ?? '',
			lastName: legacy.lastName ?? '',
			userName: legacy.userName ?? '',
			email: legacy.email ?? '',
			phoneNumber: str(legacy.phoneNumber),
			gender: null,
			dateOfBirth: null,
			isActive: bool(legacy.isActive),
			roles: (legacy.roles ?? []).map((r) => r?.name).filter((n): n is string => !!n),
			profilePhotoUrl: null,
		},
		party: null,
		assignment: a?.code
			? {
					electionId: official?.electionId ?? null,
					electionName: str(official?.election),
					resultType: a.resultType ? (a.resultType.toUpperCase() as ResultType) : null,
					code: str(a.code),
					name: str(a.name),
					isCompleted: bool(a.isCompleted),
					// The assignment payload carries no ancestor names — only the leaf's.
					// The ladder renders code-only rungs rather than inventing them.
					stateName: null,
					lgaName: null,
					wardName: null,
					pollingUnitName: null,
				}
			: null,
		payout: null,
		verification: null,
		readiness: null,
	};
};
